/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */

import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom";

import { createUploadLink } from "apollo-upload-client";
import { ApolloClient, ApolloLink, ApolloProvider, ErrorPolicy, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

// region Libraries
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Libraries
// region Hooks
// endregion Hooks
// region Atoms
import ExpiredSession from "@atoms/ExpiredSession";
// endregion Atoms
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Helper
import IGraphQLExtensionError from "@store/interfaces/IGraphQLExtensionError";
import { useAuth } from "@hooks/useAuth";
import utils from "@helpers/utils";
import { ECommandResultStatus } from "./helpers/types.graphql";
// endregion Helper

const TelluriaApolloProvider: React.FC = ({ children }) => {

  const { addToast } = useToast();
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const baseOptions = useMemo(() => ({ errorPolicy: "all" as ErrorPolicy }), []);
  const link = ApolloLink.from([
    onError(({
      graphQLErrors,
      networkError
    }) => {

      if (networkError) {

        addToast({
          type: "error",
          title: "Connection not established",
          overwrite: true
        });

      }
      if (graphQLErrors) {

        // Add all errors in a single string
        graphQLErrors.forEach(({
          message,
          extensions
        }) => {

          // Normalize type of extensions with Default return execution error of GraphQL
          const error = extensions as IGraphQLExtensionError;

          error.data = {
            status: error?.data?.status ?? ECommandResultStatus.ERROR,
            errorCode: error?.data?.errorCode ?? "9999"
          };

          // If token expired, force user to login again
          if (error.code === "authorization") {

            ReactDOM.render(
              <ExpiredSession />,
              document.getElementById("extra")
            );

            return;

          }

          addToast({
            type:
              error.data.status === ECommandResultStatus.ERROR
                ? "error"
                : "info",
            title: error.data.errorCode,
            description: message,
            overwrite: false
          });
        });
      }
    }),
    createUploadLink({
      uri: utils.getBackendBaseUrl(),
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Accept-Language": i18n.language
      }
    })
  ]);

  // Create the apollo client
  const apolloClient = useMemo(
    () => new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        mutate: baseOptions,
        query: baseOptions,
        watchQuery: baseOptions
      }
    }),
    [link, baseOptions]
  );

  // When change language, update the headers
  useEffect(() => {

    apolloClient.setLink(link);

    return () => {
      apolloClient.stop();
    };

  }, [i18n.language, apolloClient, link]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default TelluriaApolloProvider;
